import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { homePathForRole } from "@/lib/authRouting";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Terminal, ArrowRight } from "lucide-react";

export default function Login({ initialTab = "login" }) {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");

  useEffect(() => {
    if (user) {
      const target = location.state?.from?.pathname || homePathForRole(user.role);
      navigate(target, { replace: true });
    }
  }, [user, navigate, location]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(loginEmail, loginPassword);
      toast.success(`Bem-vindo, ${u.name}!`);
      navigate(homePathForRole(u.role));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const referralCode = new URLSearchParams(location.search).get("ref");
      const u = await register(regName, regEmail, regPassword, referralCode);
      toast.success(`Conta criada — bem-vindo, ${u.name}!`);
      navigate(homePathForRole(u.role));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const referralCode = new URLSearchParams(location.search).get("ref");
    const referralQuery = referralCode ? `?ref=${encodeURIComponent(referralCode)}` : "";
    const redirectUrl = window.location.origin + `/dashboard${referralQuery}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#09090B]">
      {/* Left brand pane */}
      <div className="hidden md:flex flex-col justify-between border-r border-white/10 p-10 bg-[#0c0c0e] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <Link to="/" className="flex items-center gap-2 relative" data-testid="login-brand-link">
          <div className="h-9 w-9 flex items-center justify-center border border-white/20">
            <Terminal className="h-4 w-4" strokeWidth={2} />
          </div>
          <span className="font-heading font-black tracking-tighter text-xl">Flix VPS</span>
        </Link>
        <div className="relative">
          <h2 className="font-heading text-4xl font-bold tracking-tighter leading-tight">
            Painel de gerenciamento <br /> ao estilo dos grandes.
          </h2>
          <p className="text-zinc-400 mt-4 max-w-md">
            Clone WHMCS em português, feito para operações modernas: PIX,
            provisionamento instantâneo, suporte com IA.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 text-xs font-mono">
            <div className="border border-white/10 p-4"><div className="text-zinc-500 uppercase tracking-widest">Uptime</div><div className="text-xl mt-1">99.99%</div></div>
            <div className="border border-white/10 p-4"><div className="text-zinc-500 uppercase tracking-widest">Setup</div><div className="text-xl mt-1">60s</div></div>
            <div className="border border-white/10 p-4"><div className="text-zinc-500 uppercase tracking-widest">Suporte</div><div className="text-xl mt-1">24/7</div></div>
          </div>
        </div>
        <div className="text-xs font-mono text-zinc-500 relative">© 2026 Flix VPS</div>
      </div>

      {/* Right form pane */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// autenticação</div>
            <h1 className="font-heading text-3xl font-bold tracking-tighter">Entre na sua conta</h1>
            <p className="text-sm text-zinc-500 mt-2">Ou crie uma nova em segundos.</p>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 bg-[#121214] border border-white/10 rounded-sm h-10 p-0">
              <TabsTrigger value="login" data-testid="tab-login" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black">Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black">Criar conta</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6 space-y-4">
              <form onSubmit={doLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-xs font-mono uppercase tracking-widest text-zinc-400">Email</Label>
                  <Input id="login-email" data-testid="login-email-input" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required className="rounded-sm bg-[#121214] border-white/10 focus:border-white h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-xs font-mono uppercase tracking-widest text-zinc-400">Senha</Label>
                  <Input id="login-password" data-testid="login-password-input" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required className="rounded-sm bg-[#121214] border-white/10 focus:border-white h-11" />
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-sm bg-white text-black hover:bg-zinc-200 h-11 btn-sharp" data-testid="login-submit-btn">
                  {loading ? "Entrando…" : (<>Entrar <ArrowRight className="ml-2 h-4 w-4" /></>)}
                </Button>
              </form>
              <div className="text-xs text-zinc-500 font-mono border border-white/10 p-3 bg-[#121214]">
                <div className="uppercase tracking-widest mb-1 text-zinc-400">Suporte</div>
                Problemas para entrar? Contate suporte@flixvps.com.br
              </div>
            </TabsContent>

            <TabsContent value="register" className="mt-6 space-y-4">
              <form onSubmit={doRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reg-name" className="text-xs font-mono uppercase tracking-widest text-zinc-400">Nome</Label>
                  <Input id="reg-name" data-testid="register-name-input" value={regName} onChange={(e) => setRegName(e.target.value)} required className="rounded-sm bg-[#121214] border-white/10 focus:border-white h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email" className="text-xs font-mono uppercase tracking-widest text-zinc-400">Email</Label>
                  <Input id="reg-email" data-testid="register-email-input" type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} required className="rounded-sm bg-[#121214] border-white/10 focus:border-white h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password" className="text-xs font-mono uppercase tracking-widest text-zinc-400">Senha</Label>
                  <Input id="reg-password" data-testid="register-password-input" type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required minLength={6} className="rounded-sm bg-[#121214] border-white/10 focus:border-white h-11" />
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-sm bg-white text-black hover:bg-zinc-200 h-11 btn-sharp" data-testid="register-submit-btn">
                  {loading ? "Criando…" : "Criar conta"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 border-t border-white/10" />
            <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">ou</span>
            <div className="flex-1 border-t border-white/10" />
          </div>

          <Button onClick={googleLogin} variant="outline" className="w-full rounded-sm border-white/20 hover:bg-white hover:text-black h-11 btn-sharp" data-testid="google-login-btn">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="currentColor" d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972-3.332 0-6.033-2.701-6.033-6.032s2.701-6.032 6.033-6.032c1.498 0 2.866.549 3.921 1.453l2.814-2.814C17.503 2.988 15.139 2 12.545 2 7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" /></svg>
            Entrar com Google
          </Button>

          <div className="text-center mt-6 text-xs text-zinc-500">
            <Link to="/" className="hover:text-white">← Voltar para o site</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
